import React from 'react';
import { Alert, Fade } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { AccountData, PassOut } from '../../api';
import {
  getDaysUntilExpiry,
  getPassOrderPage,
  getPassOrderPageName,
  isMembership,
  isPassExpiringSoon,
} from '../utils/passUtils';
import { useLanguage } from '../../utils/GeneralUtils';

type ExpirationBannerProps = {
  passes: PassOut[];
  account: AccountData;
};

export const ExpirationBanner = ({ passes, account }: ExpirationBannerProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  // A membership's expiry date is the end of its billing cycle, not an expiry, so "expires in 3
  // days" never applies to one -- an ending or paused membership says so in its own summary line.
  const expiringSoonPasses = passes.filter((p) => {
    const passOrderPage = getPassOrderPage(account, p.order_page_id);
    return isPassExpiringSoon(p, passOrderPage) && !isMembership(passOrderPage);
  });

  if (expiringSoonPasses.length === 0) return null;

  return (
    <>
      {expiringSoonPasses.map((pass) => {
        const passOrderPage = getPassOrderPage(account, pass.order_page_id);
        const daysLeft = getDaysUntilExpiry(pass, passOrderPage);
        const passName = getPassOrderPageName(passOrderPage, language, t);
        return (
          <Fade in timeout={1000} key={pass.id}>
            <Alert severity="warning" variant="outlined" sx={{ width: '100%' }}>
              <Typography>{t('manage_passes.pass.expiresIn', { name: passName, days: daysLeft })}</Typography>
            </Alert>
          </Fade>
        );
      })}
    </>
  );
};
